/**
 * BabyCareEntryModal
 *
 * Modal for creating new baby care entries.
 * Shows different form fields based on care type.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Loader2, Check, Calendar, Clock, Bell, Moon,
  Smile, Meh, Frown, ChevronDown, ChevronUp, ArrowLeft,
  BedDouble, Baby as BabyIcon, ShoppingBag, Car, Heart,
  Stethoscope, ThermometerSun, AlertTriangle, Syringe, RotateCcw, MapPin,
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useBabyCare, CareType, FeedingType, FeedingSide, DiaperType, SleepQuality, TemperatureLocation, BabyCareEntry } from '../index';
import { scheduleSleepReminder, SLEEP_REMINDER_OPTIONS } from '@/lib/sleepReminder';
import { isNative } from '@/lib/capacitor';
import { usePregnancyStore } from '@/store/pregnancyStore';
import { getBabiesFromPregnancy } from '@/lib/babyUtils';
import type {
  FeedbackEase,
  FeedbackTiming,
  FallAsleepDuration,
  SleepContext,
  ContextTag,
  SleepFeedback,
} from '@/features/sleep-plan/lib/sleepScoring';

interface BabyCareEntryModalProps {
  careType: CareType;
  pregnancyId: string;
  onClose: () => void;
  onSleepTimerStarted?: () => void;
  /** Sleep plan mode — when 'learn' or 'active', feedback steps are shown after sleep recording */
  sleepPlanMode?: 'observe' | 'learn' | 'active';
  /** Whether premium features are available (for context tags) */
  sleepPlanPremium?: boolean;
  /** Baby name for feedback title */
  sleepPlanBabyName?: string;
  /** Callback to submit sleep feedback after recording */
  onSubmitSleepFeedback?: (entryId: string, feedback: SleepFeedback) => Promise<void>;
  /** When set, modal opens in edit mode, prefilled with this entry */
  editEntry?: BabyCareEntry;
  /** Pre-select a specific baby index (for multi-child households) */
  initialBabyIndex?: number;
}

const CARE_TITLE_KEYS: Record<CareType, string> = {
  feeding: 'entryModal.feedingTitle',
  diaper: 'entryModal.diaperTitle',
  sleep: 'entryModal.sleepTitle',
  medication: 'entryModal.medicationTitle',
  temperature: 'entryModal.temperatureTitle',
  notes: 'entryModal.noteTitle',
};

// Quick amount presets for bottle and solid
const BOTTLE_AMOUNTS = [90, 120, 150];
const SOLID_AMOUNTS = [50, 100, 150];

// Sleep context option list with icons
const SLEEP_CONTEXT_OPTIONS: { value: SleepContext; icon: typeof BedDouble }[] = [
  { value: 'bed', icon: BedDouble },
  { value: 'arms', icon: BabyIcon },
  { value: 'stroller', icon: ShoppingBag },
  { value: 'car', icon: Car },
  { value: 'carrier', icon: Heart },
  { value: 'nursing', icon: Heart },
];

const CONTEXT_TAG_OPTIONS: { value: ContextTag; icon: typeof AlertTriangle }[] = [
  { value: 'teething', icon: Stethoscope },
  { value: 'growth_spurt', icon: ThermometerSun },
  { value: 'sick', icon: Stethoscope },
  { value: 'bad_night', icon: AlertTriangle },
  { value: 'vaccination', icon: Syringe },
  { value: 'regression', icon: RotateCcw },
  { value: 'new_place', icon: MapPin },
];

export function BabyCareEntryModal({ careType, pregnancyId, onClose, onSleepTimerStarted, sleepPlanMode, sleepPlanPremium, sleepPlanBabyName, onSubmitSleepFeedback, editEntry, initialBabyIndex }: BabyCareEntryModalProps) {
  const { t } = useTranslation('babyCare');
  const { t: tSleep } = useTranslation('sleepPlan');
  const { addEntry, updateEntry, isCreating, isBabyAsleep } = useBabyCare();
  const isEditing = !!editEntry;
  const { lastCompletedPregnancy } = usePregnancyStore();
  const babies = getBabiesFromPregnancy(lastCompletedPregnancy);
  const hasMultipleBabies = babies.length > 1;

  // Whether to show feedback steps after sleep recording
  // Skip feedback flow entirely when editing an existing entry
  const showFeedbackSteps = !isEditing && careType === 'sleep' && onSubmitSleepFeedback && (sleepPlanMode === 'learn' || sleepPlanMode === 'active');

  // Helpers to extract local date/time strings from an ISO timestamp
  const isoToLocalDate = (iso: string): string => {
    const d = new Date(iso);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const isoToLocalTime = (iso: string): string => {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedBabyIndex, setSelectedBabyIndex] = useState<number>(editEntry?.baby_index ?? initialBabyIndex ?? 0);

  // Sleep feedback state
  const [feedbackStep, setFeedbackStep] = useState<'none' | 'ease' | 'details'>('none');
  const [savedEntryId, setSavedEntryId] = useState<string | null>(null);
  const [feedbackEase, setFeedbackEase] = useState<FeedbackEase | null>(null);
  const [feedbackTiming, setFeedbackTiming] = useState<FeedbackTiming | null>(null);
  const [feedbackFallAsleepDuration, setFeedbackFallAsleepDuration] = useState<FallAsleepDuration | null>(null);
  const [feedbackSleepContext, setFeedbackSleepContext] = useState<SleepContext[]>([]);
  const [feedbackContextTags, setFeedbackContextTags] = useState<ContextTag[]>([]);
  const [showOptionalTags, setShowOptionalTags] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  // Time selection state
  // When editing a sleep entry that was already completed, we use the ended_at time
  // as the "custom" end time. For other care types we use recorded_at.
  const [useCustomTime, setUseCustomTime] = useState(() => {
    if (!editEntry) return false;
    if (editEntry.care_type === 'sleep') return editEntry.ended_at !== null;
    return true;
  });
  const [customDate, setCustomDate] = useState(() => {
    if (editEntry) {
      const src = editEntry.care_type === 'sleep' && editEntry.ended_at
        ? editEntry.ended_at
        : editEntry.recorded_at;
      return isoToLocalDate(src);
    }
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [customTime, setCustomTime] = useState(() => {
    if (editEntry) {
      const src = editEntry.care_type === 'sleep' && editEntry.ended_at
        ? editEntry.ended_at
        : editEntry.recorded_at;
      return isoToLocalTime(src);
    }
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });

  // Form state
  const [feedingType, setFeedingType] = useState<FeedingType>('breast');
  const [feedingSide, setFeedingSide] = useState<FeedingSide>('left');
  const [feedingAmountMl, setFeedingAmountMl] = useState<number | undefined>();
  const [feedingDurationMinutes, setFeedingDurationMinutes] = useState<number | undefined>();
  const [customAmountInput, setCustomAmountInput] = useState('');

  const [diaperType, setDiaperType] = useState<DiaperType>('wet');

  const [sleepQuality, setSleepQuality] = useState<SleepQuality | undefined>(
    editEntry?.sleep_quality ?? undefined,
  );
  // When editing a completed sleep entry, show the "Schon aufgewacht" form
  const [startSleepNow, setStartSleepNow] = useState(() => {
    if (editEntry?.care_type === 'sleep') return editEntry.ended_at === null;
    return true;
  });
  const [sleepReminderMinutes, setSleepReminderMinutes] = useState(0);
  // For "Jetzt eingeschlafen" - allow setting custom start time
  const [useCustomStartTime, setUseCustomStartTime] = useState(() => {
    return editEntry?.care_type === 'sleep' && editEntry.ended_at === null;
  });
  const [customStartTimeValue, setCustomStartTimeValue] = useState(() => {
    if (editEntry?.care_type === 'sleep' && editEntry.ended_at === null) {
      return isoToLocalTime(editEntry.recorded_at);
    }
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  });
  // For "Schon aufgewacht" - we need sleep start time (backdating)
  const [sleepStartDate, setSleepStartDate] = useState(() => {
    if (editEntry?.care_type === 'sleep') return isoToLocalDate(editEntry.recorded_at);
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  });
  const [sleepStartTime, setSleepStartTime] = useState(() => {
    if (editEntry?.care_type === 'sleep') return isoToLocalTime(editEntry.recorded_at);
    const now = new Date();
    const earlier = new Date(now.getTime() - 60 * 60 * 1000); // 1 hour ago default
    return `${String(earlier.getHours()).padStart(2, '0')}:${String(earlier.getMinutes()).padStart(2, '0')}`;
  });

  const [medicationName, setMedicationName] = useState('');
  const [medicationDose, setMedicationDose] = useState('');

  const [temperatureValue, setTemperatureValue] = useState<number>(37.0);
  const [temperatureLocation, setTemperatureLocation] = useState<TemperatureLocation>('ear');

  const [notes, setNotes] = useState(editEntry?.notes ?? '');

  // Get the recorded_at timestamp based on user selection
  const getRecordedAt = (): string => {
    if (useCustomTime) {
      return new Date(`${customDate}T${customTime}`).toISOString();
    }
    return new Date().toISOString();
  };

  // Get sleep start time for "Schon aufgewacht" entries
  const getSleepStartTime = (): string => {
    return new Date(`${sleepStartDate}T${sleepStartTime}`).toISOString();
  };

  // Handle quick amount button click
  const handleQuickAmount = (amount: number) => {
    setFeedingAmountMl(amount);
    setCustomAmountInput('');
  };

  // Handle custom amount input
  const handleCustomAmountChange = (value: string) => {
    setCustomAmountInput(value);
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue > 0) {
      setFeedingAmountMl(numValue);
    } else if (value === '') {
      setFeedingAmountMl(undefined);
    }
  };

  const handleSubmit = async () => {
    // Build the entry object with base fields
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const entry: any = {
      pregnancy_id: pregnancyId,
      care_type: careType,
      baby_index: selectedBabyIndex,
      notes: notes || undefined,
    };

    // Add type-specific fields
    if (careType === 'feeding') {
      if (useCustomTime) {
        entry.recorded_at = getRecordedAt();
      }
      Object.assign(entry, {
        feeding_type: feedingType,
        feeding_side: feedingType === 'breast' ? feedingSide : undefined,
        feeding_amount_ml: feedingAmountMl,
        feeding_duration_minutes: feedingDurationMinutes,
      });
    } else if (careType === 'diaper') {
      if (useCustomTime) {
        entry.recorded_at = getRecordedAt();
      }
      Object.assign(entry, {
        diaper_type: diaperType,
      });
    } else if (careType === 'sleep') {
      if (startSleepNow) {
        // Start timer - either now or from custom time
        if (useCustomStartTime && customStartTimeValue) {
          const today = new Date();
          const [hours, minutes] = customStartTimeValue.split(':').map(Number);
          const startTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
          // If selected time is in the future, assume it was yesterday
          if (startTime > new Date()) {
            startTime.setDate(startTime.getDate() - 1);
          }
          entry.recorded_at = startTime.toISOString();
        }
        Object.assign(entry, {
          sleep_quality: undefined,
          ended_at: null,
        });
      } else {
        // "Schon aufgewacht" - complete sleep entry with backdated start
        Object.assign(entry, {
          recorded_at: getSleepStartTime(),
          sleep_quality: sleepQuality,
          ended_at: useCustomTime ? getRecordedAt() : new Date().toISOString(),
        });
      }
    } else if (careType === 'medication') {
      if (useCustomTime) {
        entry.recorded_at = getRecordedAt();
      }
      Object.assign(entry, {
        medication_name: medicationName,
        medication_dose: medicationDose,
      });
    } else if (careType === 'temperature') {
      if (useCustomTime) {
        entry.recorded_at = getRecordedAt();
      }
      Object.assign(entry, {
        temperature_value: temperatureValue,
        temperature_location: temperatureLocation,
      });
    }
    // Notes don't have backdating

    // Edit mode: update existing entry instead of creating a new one
    if (isEditing && editEntry) {
      // Strip fields that shouldn't be part of an update payload
      const { pregnancy_id: _pid, care_type: _ct, ...updates } = entry;
      void _pid; void _ct;
      const updated = await updateEntry(editEntry.id, updates);
      if (updated) {
        setShowSuccess(true);
        setTimeout(onClose, 800);
      }
      return;
    }

    const result = await addEntry(entry);
    if (result) {
      // Schedule sleep reminder if enabled and starting timer now
      if (careType === 'sleep' && startSleepNow && sleepReminderMinutes > 0) {
        const entryId = typeof result === 'object' && result.id ? result.id : Date.now().toString();
        scheduleSleepReminder(entryId, sleepReminderMinutes);
      }

      const entryId = typeof result === 'object' && result.id ? result.id : null;

      // If sleep plan feedback is enabled, transition to feedback steps instead of closing
      if (showFeedbackSteps && entryId) {
        setSavedEntryId(entryId);
        setFeedbackStep('ease');
        // Still trigger timer started callback so the page refreshes
        if (careType === 'sleep' && startSleepNow && onSleepTimerStarted) {
          onSleepTimerStarted();
        }
        return;
      }

      setShowSuccess(true);
      setTimeout(() => {
        onClose();
        // If sleep timer was started, trigger page refresh
        if (careType === 'sleep' && startSleepNow && onSleepTimerStarted) {
          onSleepTimerStarted();
        }
      }, 1000);
    }
  };

  // Feedback helpers
  const toggleFeedbackSleepContext = (ctx: SleepContext) => {
    setFeedbackSleepContext(prev =>
      prev.includes(ctx) ? prev.filter(c => c !== ctx) : [...prev, ctx],
    );
  };

  const toggleFeedbackContextTag = (tag: ContextTag) => {
    setFeedbackContextTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag],
    );
  };

  const handleFeedbackEaseSelect = (selected: FeedbackEase) => {
    setFeedbackEase(selected);
    setTimeout(() => setFeedbackStep('details'), 200);
  };

  const handleFeedbackSubmit = async (easeOnly?: boolean) => {
    if (!feedbackEase || !savedEntryId || !onSubmitSleepFeedback) return;
    setIsSubmittingFeedback(true);

    const feedback: SleepFeedback = easeOnly
      ? { ease: feedbackEase }
      : {
          ease: feedbackEase,
          timing: feedbackTiming ?? undefined,
          fallAsleepDuration: feedbackFallAsleepDuration ?? undefined,
          sleepContext: feedbackSleepContext.length > 0 ? feedbackSleepContext : undefined,
          contextTags: feedbackContextTags.length > 0 ? feedbackContextTags : undefined,
        };

    await onSubmitSleepFeedback(savedEntryId, feedback);
    setIsSubmittingFeedback(false);
    setShowSuccess(true);
    setTimeout(onClose, 800);
  };

  const handleFeedbackSkip = () => {
    setShowSuccess(true);
    setTimeout(onClose, 800);
  };

  // Check if backdating should be shown for this care type (not for notes or sleep)
  const showBackdating = careType !== 'notes' && careType !== 'sleep';

  // Get the available quick amounts based on feeding type
  const quickAmounts = feedingType === 'bottle' ? BOTTLE_AMOUNTS : SOLID_AMOUNTS;

  const modalContent = (
    <>
      {/* Backdrop */}
      <div className="bottom-sheet-backdrop" onClick={onClose} />

      {/* Modal Container */}
      <div className="bottom-sheet-container">
        <div className="bottom-sheet-modal">
          {/* Header */}
          <div className="bottom-sheet-header">
            <h2>
              {feedbackStep === 'ease'
                ? tSleep('feedback.easeTitle', { name: sleepPlanBabyName || 'Baby' })
                : feedbackStep === 'details'
                ? tSleep('feedback.detailsTitle')
                : isEditing
                ? t('entryModal.editTitle')
                : t(CARE_TITLE_KEYS[careType])}
            </h2>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="bottom-sheet-content">
            {showSuccess ? (
              <div className="flex flex-col items-center py-8">
                <div className="w-16 h-16 rounded-full bg-green-500 flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-white" />
                </div>
                <p className="text-white text-lg font-medium">Gespeichert!</p>
              </div>
            ) : feedbackStep === 'ease' ? (
              <div className="space-y-4">
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => handleFeedbackEaseSelect('easy')}
                    className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-[15px] transition-all ${
                      feedbackEase === 'easy' ? 'bg-green-500/20 border border-green-500/50' : 'glass-effect hover:bg-white/15'
                    }`}
                  >
                    <Smile className={`w-8 h-8 ${feedbackEase === 'easy' ? 'text-green-400' : 'text-white/60'}`} />
                    <span className="text-white text-sm font-medium">{tSleep('feedback.easy')}</span>
                  </button>
                  <button
                    onClick={() => handleFeedbackEaseSelect('normal')}
                    className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-[15px] transition-all ${
                      feedbackEase === 'normal' ? 'bg-yellow-500/20 border border-yellow-500/50' : 'glass-effect hover:bg-white/15'
                    }`}
                  >
                    <Meh className={`w-8 h-8 ${feedbackEase === 'normal' ? 'text-yellow-400' : 'text-white/60'}`} />
                    <span className="text-white text-sm font-medium">{tSleep('feedback.normal')}</span>
                  </button>
                  <button
                    onClick={() => handleFeedbackEaseSelect('hard')}
                    className={`flex-1 flex flex-col items-center gap-2 p-4 rounded-[15px] transition-all ${
                      feedbackEase === 'hard' ? 'bg-red-500/20 border border-red-500/50' : 'glass-effect hover:bg-white/15'
                    }`}
                  >
                    <Frown className={`w-8 h-8 ${feedbackEase === 'hard' ? 'text-red-400' : 'text-white/60'}`} />
                    <span className="text-white text-sm font-medium">{tSleep('feedback.hard')}</span>
                  </button>
                </div>
              </div>
            ) : feedbackStep === 'details' ? (
              <div className="space-y-5 overflow-y-auto" style={{ maxHeight: '50vh' }}>
                {/* Timing feel */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">{tSleep('feedback.timingLabel')}</label>
                  <div className="flex gap-2">
                    {(['too_early', 'just_right', 'too_late'] as FeedbackTiming[]).map(val => (
                      <button
                        key={val}
                        onClick={() => setFeedbackTiming(feedbackTiming === val ? null : val)}
                        className={`flex-1 py-2.5 rounded-[10px] text-sm font-medium transition-colors ${
                          feedbackTiming === val ? 'bg-pp-orange text-white' : 'bg-white/10 text-white/70'
                        }`}
                      >
                        {tSleep(`feedback.timing.${val}`)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fall asleep duration */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">{tSleep('feedback.durationLabel')}</label>
                  <div className="flex gap-2">
                    {(['under_5', '5_to_15', '15_to_30', 'over_30'] as FallAsleepDuration[]).map(val => (
                      <button
                        key={val}
                        onClick={() => setFeedbackFallAsleepDuration(feedbackFallAsleepDuration === val ? null : val)}
                        className={`flex-1 py-2.5 rounded-[10px] text-xs font-medium transition-colors ${
                          feedbackFallAsleepDuration === val ? 'bg-pp-orange text-white' : 'bg-white/10 text-white/70'
                        }`}
                      >
                        {tSleep(`feedback.duration.${val}`)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sleep context */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">{tSleep('feedback.contextLabel')}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {SLEEP_CONTEXT_OPTIONS.map(({ value, icon: Icon }) => (
                      <button
                        key={value}
                        onClick={() => toggleFeedbackSleepContext(value)}
                        className={`flex items-center gap-1.5 py-2 px-3 rounded-[10px] text-xs font-medium transition-colors ${
                          feedbackSleepContext.includes(value) ? 'bg-pp-orange text-white' : 'bg-white/10 text-white/70'
                        }`}
                      >
                        <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                        {tSleep(`feedback.context.${value}`)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Context tags (premium only) */}
                {sleepPlanPremium && (
                  <div>
                    <button
                      onClick={() => setShowOptionalTags(!showOptionalTags)}
                      className="flex items-center gap-2 text-white/60 text-sm mb-2"
                    >
                      {showOptionalTags ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      {tSleep('feedback.specialCircumstances')}
                    </button>
                    {showOptionalTags && (
                      <div className="flex flex-wrap gap-2">
                        {CONTEXT_TAG_OPTIONS.map(({ value, icon: Icon }) => (
                          <button
                            key={value}
                            onClick={() => toggleFeedbackContextTag(value)}
                            className={`flex items-center gap-1.5 py-2 px-3 rounded-[10px] text-xs font-medium transition-colors ${
                              feedbackContextTags.includes(value) ? 'bg-pp-orange text-white' : 'bg-white/10 text-white/70'
                            }`}
                          >
                            <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                            {tSleep(`feedback.tags.${value}`)}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {/* Baby Selector (multi-child) */}
                {hasMultipleBabies && (
                  <div>
                    <label className="block text-white/70 text-sm mb-2">{t('entryModal.babySelect')}</label>
                    <div className="flex gap-2">
                      {babies.map((baby) => {
                        const sleeping = isBabyAsleep(baby.index);
                        const isSelected = selectedBabyIndex === baby.index;
                        // For sleep "Jetzt eingeschlafen": disable babies that are already sleeping
                        const isDisabledForSleep = careType === 'sleep' && startSleepNow && sleeping;

                        return (
                          <button
                            key={baby.index}
                            onClick={() => !isDisabledForSleep && setSelectedBabyIndex(baby.index)}
                            disabled={isDisabledForSleep}
                            className={`flex-1 py-3 rounded-[10px] font-medium transition-colors relative ${
                              isSelected
                                ? 'bg-pp-orange text-white'
                                : isDisabledForSleep
                                ? 'bg-white/5 text-white/30 cursor-not-allowed'
                                : 'bg-white/10 text-white/70'
                            }`}
                          >
                            {baby.name}
                            {sleeping && (
                              <Moon className="w-3 h-3 inline ml-1 text-pp-orange" />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Feeding Form */}
                {careType === 'feeding' && (
                  <>
                    {/* Feeding Type */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">{t('entryModal.feedingType')}</label>
                      <div className="flex gap-2">
                        {(['breast', 'bottle', 'solid'] as FeedingType[]).map((type) => (
                          <button
                            key={type}
                            onClick={() => {
                              setFeedingType(type);
                              // Reset amount when switching types
                              setFeedingAmountMl(undefined);
                              setCustomAmountInput('');
                            }}
                            className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                              feedingType === type
                                ? 'bg-pp-orange text-white'
                                : 'bg-white/10 text-white/70'
                            }`}
                          >
                            {type === 'breast' ? t('entryModal.breastfeeding') : type === 'bottle' ? t('entryModal.bottle') : t('entryModal.solidFood')}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Breast Side */}
                    {feedingType === 'breast' && (
                      <div>
                        <label className="block text-white/70 text-sm mb-2">{t('entryModal.side')}</label>
                        <div className="flex gap-2">
                          {(['left', 'right', 'both'] as FeedingSide[]).map((side) => (
                            <button
                              key={side}
                              onClick={() => setFeedingSide(side)}
                              className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                                feedingSide === side
                                  ? 'bg-pp-orange text-white'
                                  : 'bg-white/10 text-white/70'
                              }`}
                            >
                              {side === 'left' ? t('entryModal.left') : side === 'right' ? t('entryModal.right') : t('entryModal.both')}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Amount (for bottle/solid) with quick buttons */}
                    {feedingType !== 'breast' && (
                      <div>
                        <label className="block text-white/70 text-sm mb-2">{t('entryModal.amount')}</label>
                        {/* Quick amount buttons */}
                        <div className="flex gap-2 mb-3">
                          {quickAmounts.map((amount) => (
                            <button
                              key={amount}
                              onClick={() => handleQuickAmount(amount)}
                              className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                                feedingAmountMl === amount && customAmountInput === ''
                                  ? 'bg-pp-orange text-white'
                                  : 'bg-white/10 text-white/70 hover:bg-white/20'
                              }`}
                            >
                              {amount} ml
                            </button>
                          ))}
                        </div>
                        {/* Custom amount input */}
                        <input
                          type="number"
                          value={customAmountInput}
                          onChange={(e) => handleCustomAmountChange(e.target.value)}
                          placeholder={t('entryModal.customAmount')}
                          className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange"
                        />
                      </div>
                    )}

                    {/* Duration */}
                    <div>
                      <label className="block text-white/70 text-sm mb-2">{t('entryModal.duration')}</label>
                      <input
                        type="number"
                        value={feedingDurationMinutes || ''}
                        onChange={(e) => setFeedingDurationMinutes(e.target.value ? Number(e.target.value) : undefined)}
                        placeholder={t('entryModal.durationPlaceholder')}
                        className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange"
                      />
                    </div>
                  </>
                )}

                {/* Diaper Form */}
                {careType === 'diaper' && (
                  <div>
                    <label className="block text-white/70 text-sm mb-2">{t('entryModal.diaperType')}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {(['wet', 'dirty', 'both', 'dry'] as DiaperType[]).map((type) => (
                        <button
                          key={type}
                          onClick={() => setDiaperType(type)}
                          className={`py-3 rounded-[10px] font-medium transition-colors ${
                            diaperType === type
                              ? 'bg-pp-orange text-white'
                              : 'bg-white/10 text-white/70'
                          }`}
                        >
                          {type === 'wet' ? t('entryModal.wet') : type === 'dirty' ? t('entryModal.soiled') : type === 'both' ? t('entryModal.both_diaper') : t('entryModal.dry')}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sleep Form */}
                {careType === 'sleep' && (
                  <>
                    {/* Mode Selection */}
                    <div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setStartSleepNow(true)}
                          className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                            startSleepNow
                              ? 'bg-pp-orange text-white'
                              : 'bg-white/10 text-white/70'
                          }`}
                        >
                          {t('entryModal.sleepNow')}
                        </button>
                        <button
                          onClick={() => setStartSleepNow(false)}
                          className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                            !startSleepNow
                              ? 'bg-pp-orange text-white'
                              : 'bg-white/10 text-white/70'
                          }`}
                        >
                          {t('entryModal.sleepAlreadyWoke')}
                        </button>
                      </div>
                    </div>

                    {/* "Jetzt eingeschlafen" - Time selection */}
                    {startSleepNow && (
                      <div>
                        <label className="block text-white/70 text-sm mb-2">
                          {t('entryModal.sleepSince', { name: babies[selectedBabyIndex]?.name || 'Baby' })}
                        </label>
                        <div className="flex gap-2 mb-2">
                          <button
                            onClick={() => setUseCustomStartTime(false)}
                            className={`flex-1 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                              !useCustomStartTime
                                ? 'bg-pp-orange text-white'
                                : 'bg-white/10 text-white/70'
                            }`}
                          >
                            {t('entryModal.now')}
                          </button>
                          <button
                            onClick={() => setUseCustomStartTime(true)}
                            className={`flex-1 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                              useCustomStartTime
                                ? 'bg-pp-orange text-white'
                                : 'bg-white/10 text-white/70'
                            }`}
                          >
                            {t('entryModal.choose')}
                          </button>
                        </div>
                        {useCustomStartTime && (
                          <div className="relative">
                            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            <input
                              type="time"
                              value={customStartTimeValue}
                              onChange={(e) => setCustomStartTimeValue(e.target.value)}
                              className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Sleep reminder option - only show when starting timer now */}
                    {startSleepNow && isNative && (
                      <div>
                        <label className="block text-white/70 text-sm mb-2">
                          <Bell className="w-4 h-4 inline mr-1" />
                          {t('entryModal.sleepReminder')}
                        </label>
                        <div className="flex gap-2 flex-wrap">
                          {SLEEP_REMINDER_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              onClick={() => setSleepReminderMinutes(option.value)}
                              className={`px-4 py-2 rounded-[10px] font-medium transition-colors ${
                                sleepReminderMinutes === option.value
                                  ? 'bg-pp-orange text-white'
                                  : 'bg-white/10 text-white/70'
                              }`}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                        {sleepReminderMinutes > 0 && (
                          <p className="text-white/50 text-xs mt-2">
                            {t('entryModal.sleepReminderHint', { minutes: sleepReminderMinutes })}
                          </p>
                        )}
                      </div>
                    )}

                    {!startSleepNow && (
                      <>
                        {/* Sleep start time for backdating */}
                        <div>
                          <label className="block text-white/70 text-sm mb-2">{t('entryModal.sleepStartTime')}</label>
                          <div className="flex gap-2">
                            <div className="flex-1 relative">
                              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                              <input
                                type="date"
                                value={sleepStartDate}
                                onChange={(e) => setSleepStartDate(e.target.value)}
                                className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                              />
                            </div>
                            <div className="flex-1 relative">
                              <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                              <input
                                type="time"
                                value={sleepStartTime}
                                onChange={(e) => setSleepStartTime(e.target.value)}
                                className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Wake up time (optional backdating) */}
                        <div>
                          <label className="block text-white/70 text-sm mb-2">{t('entryModal.sleepEndTime')}</label>
                          <div className="flex gap-2 mb-2">
                            <button
                              onClick={() => setUseCustomTime(false)}
                              className={`flex-1 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                                !useCustomTime
                                  ? 'bg-pp-orange text-white'
                                  : 'bg-white/10 text-white/70'
                              }`}
                            >
                              {t('entryModal.now')}
                            </button>
                            <button
                              onClick={() => setUseCustomTime(true)}
                              className={`flex-1 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                                useCustomTime
                                  ? 'bg-pp-orange text-white'
                                  : 'bg-white/10 text-white/70'
                              }`}
                            >
                              {t('entryModal.choose')}
                            </button>
                          </div>
                          {useCustomTime && (
                            <div className="flex gap-2">
                              <div className="flex-1 relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                <input
                                  type="date"
                                  value={customDate}
                                  onChange={(e) => setCustomDate(e.target.value)}
                                  className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                                />
                              </div>
                              <div className="flex-1 relative">
                                <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                                <input
                                  type="time"
                                  value={customTime}
                                  onChange={(e) => setCustomTime(e.target.value)}
                                  className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="block text-white/70 text-sm mb-2">{t('entryModal.sleepQuality')}</label>
                          <div className="flex gap-2">
                            {(['good', 'okay', 'poor'] as SleepQuality[]).map((quality) => (
                              <button
                                key={quality}
                                onClick={() => setSleepQuality(quality)}
                                className={`flex-1 py-3 rounded-[10px] font-medium transition-colors ${
                                  sleepQuality === quality
                                    ? 'bg-pp-orange text-white'
                                    : 'bg-white/10 text-white/70'
                                }`}
                              >
                                {quality === 'good' ? t('entryModal.qualityGood') : quality === 'okay' ? t('entryModal.qualityOkay') : t('entryModal.qualityBad')}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* Medication Form */}
                {careType === 'medication' && (
                  <>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">{t('entryModal.medicationName')}</label>
                      <input
                        type="text"
                        value={medicationName}
                        onChange={(e) => setMedicationName(e.target.value)}
                        placeholder={t('entryModal.medicationPlaceholder')}
                        className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange"
                      />
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">{t('entryModal.dosage')}</label>
                      <input
                        type="text"
                        value={medicationDose}
                        onChange={(e) => setMedicationDose(e.target.value)}
                        placeholder={t('entryModal.dosagePlaceholder')}
                        className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange"
                      />
                    </div>
                  </>
                )}

                {/* Temperature Form */}
                {careType === 'temperature' && (
                  <>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">{t('entryModal.temperatureLabel')}</label>
                      <input
                        type="number"
                        step="0.1"
                        value={temperatureValue}
                        onChange={(e) => setTemperatureValue(Number(e.target.value))}
                        className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange text-2xl text-center"
                      />
                      <div className="flex justify-center gap-4 mt-2">
                        <button
                          onClick={() => setTemperatureValue(v => Math.round((v - 0.1) * 10) / 10)}
                          className="w-10 h-10 bg-white/10 rounded-full text-white text-xl"
                        >
                          -
                        </button>
                        <button
                          onClick={() => setTemperatureValue(v => Math.round((v + 0.1) * 10) / 10)}
                          className="w-10 h-10 bg-white/10 rounded-full text-white text-xl"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/70 text-sm mb-2">{t('entryModal.measurementLocation')}</label>
                      <div className="grid grid-cols-2 gap-2">
                        {(['ear', 'forehead', 'armpit', 'rectal'] as TemperatureLocation[]).map((loc) => (
                          <button
                            key={loc}
                            onClick={() => setTemperatureLocation(loc)}
                            className={`py-3 rounded-[10px] font-medium transition-colors ${
                              temperatureLocation === loc
                                ? 'bg-pp-orange text-white'
                                : 'bg-white/10 text-white/70'
                            }`}
                          >
                            {loc === 'ear' ? t('entryModal.ear') : loc === 'forehead' ? t('entryModal.forehead') : loc === 'armpit' ? t('entryModal.armpit') : t('entryModal.rectal')}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* Time selection (for non-sleep, non-notes types) */}
                {showBackdating && (
                  <div>
                    <label className="block text-white/70 text-sm mb-2">{t('entryModal.timeLabel')}</label>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => setUseCustomTime(false)}
                        className={`flex-1 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                          !useCustomTime
                            ? 'bg-pp-orange text-white'
                            : 'bg-white/10 text-white/70'
                        }`}
                      >
                        {t('entryModal.now')}
                      </button>
                      <button
                        onClick={() => setUseCustomTime(true)}
                        className={`flex-1 py-2 rounded-[10px] text-sm font-medium transition-colors ${
                          useCustomTime
                            ? 'bg-pp-orange text-white'
                            : 'bg-white/10 text-white/70'
                        }`}
                      >
                        {t('entryModal.choose')}
                      </button>
                    </div>
                    {useCustomTime && (
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                          <input
                            type="date"
                            value={customDate}
                            onChange={(e) => setCustomDate(e.target.value)}
                            className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                          />
                        </div>
                        <div className="flex-1 relative">
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                          <input
                            type="time"
                            value={customTime}
                            onChange={(e) => setCustomTime(e.target.value)}
                            className="w-full py-3 pl-10 pr-4 bg-white/10 rounded-[10px] text-white outline-none focus:ring-2 focus:ring-pp-orange [color-scheme:dark]"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Notes (shown for all types) */}
                <div>
                  <label className="block text-white/70 text-sm mb-2">
                    {careType === 'notes' ? t('entryModal.noteLabel') : t('entryModal.notesOptional')}
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={careType === 'notes' ? t('entryModal.notePlaceholder') : t('entryModal.notesPlaceholder')}
                    rows={careType === 'notes' ? 4 : 2}
                    className="w-full py-3 px-4 bg-white/10 rounded-[10px] text-white placeholder:text-white/40 outline-none focus:ring-2 focus:ring-pp-orange resize-none"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          {!showSuccess && feedbackStep === 'ease' && (
            <div className="bottom-sheet-footer">
              <button
                onClick={handleFeedbackSkip}
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
              >
                {tSleep('feedback.skip')}
              </button>
            </div>
          )}
          {!showSuccess && feedbackStep === 'details' && (
            <div className="bottom-sheet-footer">
              <button
                onClick={() => setFeedbackStep('ease')}
                className="py-2.5 px-4 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30 flex items-center gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleFeedbackSubmit(true)}
                className="flex-1 py-2.5 rounded-xl bg-white/20 border border-white/25 text-white font-medium hover:bg-white/30"
              >
                {tSleep('feedback.skipDetails')}
              </button>
              <button
                onClick={() => handleFeedbackSubmit(false)}
                disabled={isSubmittingFeedback}
                className="flex-1 py-2.5 rounded-xl bg-pp-orange text-white font-medium hover:bg-pp-orange/90 disabled:opacity-50 flex items-center justify-center"
              >
                {isSubmittingFeedback ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  tSleep('feedback.save')
                )}
              </button>
            </div>
          )}
          {!showSuccess && feedbackStep === 'none' && (
            <div className="bottom-sheet-footer">
              <button
                onClick={handleSubmit}
                disabled={isCreating || (careType === 'notes' && !notes) || (careType === 'medication' && !medicationName)}
                className="w-full py-4 bg-pp-orange text-white font-semibold rounded-[15px] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t('entryModal.saving')}
                  </>
                ) : isEditing ? (
                  t('entryModal.saveChanges')
                ) : careType === 'sleep' && startSleepNow ? (
                  t('entryModal.startTimer')
                ) : (
                  t('entryModal.save')
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
}
